import { useContext, useMemo } from 'react';
import { Inbox } from '@novu/react';
import { UserContext } from 'contexts/user/UserContext';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

function NotificationInbox() {
  const history = useHistory();
  const intl = useIntl();
  const applicationIdentifier = import.meta.env.VITE_NOVU_APPLICATION_IDENTIFIER as string | undefined;

  const localization = useMemo(() => ({
    'inbox.filters.dropdownOptions.unread': intl.formatMessage({ id: 'novu.inbox.filters.dropdownOptions.unread' }),
    'inbox.filters.dropdownOptions.default': intl.formatMessage({ id: 'novu.inbox.filters.dropdownOptions.default' }),
    'inbox.filters.dropdownOptions.archived': intl.formatMessage({ id: 'novu.inbox.filters.dropdownOptions.archived' }),
    'inbox.filters.dropdownOptions.snoozed': intl.formatMessage({ id: 'novu.inbox.filters.dropdownOptions.snoozed' }),
    'inbox.filters.labels.unread': intl.formatMessage({ id: 'novu.inbox.filters.labels.unread' }),
    'inbox.filters.labels.default': intl.formatMessage({ id: 'novu.inbox.filters.labels.default' }),
    'inbox.filters.labels.archived': intl.formatMessage({ id: 'novu.inbox.filters.labels.archived' }),
    'inbox.filters.labels.snoozed': intl.formatMessage({ id: 'novu.inbox.filters.labels.snoozed' }),
    'notifications.emptyNotice': intl.formatMessage({ id: 'novu.notifications.emptyNotice' }),
    'notifications.actions.readAll': intl.formatMessage({ id: 'novu.notifications.actions.readAll' }),
    'notifications.actions.archiveAll': intl.formatMessage({ id: 'novu.notifications.actions.archiveAll' }),
    'notifications.actions.archiveRead': intl.formatMessage({ id: 'novu.notifications.actions.archiveRead' }),
    'notifications.newNotifications': ({ notificationCount }: { notificationCount: number }) => {
      if (notificationCount > 99) {
        return intl.formatMessage({ id: 'novu.notifications.newNotifications.plus' });
      }
      if (notificationCount === 1) {
        return intl.formatMessage({ id: 'novu.notifications.newNotifications.singular' });
      }
      return intl.formatMessage({ id: 'novu.notifications.newNotifications.plural' }, { notificationCount });
    },
    'notification.actions.read.tooltip': intl.formatMessage({ id: 'novu.notification.actions.read.tooltip' }),
    'notification.actions.unread.tooltip': intl.formatMessage({ id: 'novu.notification.actions.unread.tooltip' }),
    'notification.actions.archive.tooltip': intl.formatMessage({ id: 'novu.notification.actions.archive.tooltip' }),
    'notification.actions.unarchive.tooltip': intl.formatMessage({ id: 'novu.notification.actions.unarchive.tooltip' }),
    'notification.actions.snooze.tooltip': intl.formatMessage({ id: 'novu.notification.actions.snooze.tooltip' }),
    'notification.actions.unsnooze.tooltip': intl.formatMessage({ id: 'novu.notification.actions.unsnooze.tooltip' }),
    'notification.snoozedUntil': intl.formatMessage({ id: 'novu.notification.snoozedUntil' }),
    'preferences.title': intl.formatMessage({ id: 'novu.preferences.title' }),
    'preferences.emptyNotice': intl.formatMessage({ id: 'novu.preferences.emptyNotice' }),
    'preferences.global': intl.formatMessage({ id: 'novu.preferences.global' }),
    'preferences.workflow.disabled.notice': intl.formatMessage({ id: 'novu.preferences.workflow.disabled.notice' }),
    'preferences.workflow.disabled.tooltip': intl.formatMessage({ id: 'novu.preferences.workflow.disabled.tooltip' }),
    'preferences.group.info': intl.formatMessage({ id: 'novu.preferences.group.info' }),
    'snooze.datePicker.timePickerLabel': intl.formatMessage({ id: 'novu.snooze.datePicker.timePickerLabel' }),
    'snooze.datePicker.apply': intl.formatMessage({ id: 'novu.snooze.datePicker.apply' }),
    'snooze.datePicker.cancel': intl.formatMessage({ id: 'novu.snooze.datePicker.cancel' }),
    'snooze.options.anHourFromNow': intl.formatMessage({ id: 'novu.snooze.options.anHourFromNow' }),
    'snooze.datePicker.pastDateTooltip': intl.formatMessage({ id: 'novu.snooze.datePicker.pastDateTooltip' }),
    'snooze.datePicker.noDateSelectedTooltip': intl.formatMessage({ id: 'novu.snooze.datePicker.noDateSelectedTooltip' }),
    'snooze.datePicker.exceedingLimitTooltip': ({ days }: { days: number }) => {
      if (days === 1) {
        return intl.formatMessage({ id: 'novu.snooze.datePicker.exceedingLimitTooltip.singular' });
      }
      return intl.formatMessage({ id: 'novu.snooze.datePicker.exceedingLimitTooltip.plural' }, { days });
    },
    'snooze.options.customTime': intl.formatMessage({ id: 'novu.snooze.options.customTime' }),
    'snooze.options.inOneDay': intl.formatMessage({ id: 'novu.snooze.options.inOneDay' }),
    'snooze.options.inOneWeek': intl.formatMessage({ id: 'novu.snooze.options.inOneWeek' }),
  }), [intl]);

  const currentUser = useContext(UserContext)['currentUser'];
  if (!currentUser) {
    return null;
  }

  if (!applicationIdentifier) {
    console.error('VITE_NOVU_APPLICATION_IDENTIFIER is not defined');
    return null;
  }

  const subscriberId = currentUser?.id;

  if (!subscriberId) {
    return null;
  }

  return (
    <Inbox
      applicationIdentifier={applicationIdentifier}
      subscriberId={subscriberId}
      backendUrl={import.meta.env.VITE_NOVU_BACKEND_URL as string || "https://eu.api.novu.co"}
      socketUrl={import.meta.env.VITE_NOVU_SOCKET_URL as string || "wss://eu.ws.novu.co"}
      localization={localization}
      appearance={{
        variables: {
          colorPrimary: "#60b6aa",
          colorForeground: "#60b6aa"
        },
        elements: {
          preferences__button: { display: "none" }, // hide the settings button
        },
        icons: {},
      }}
      routerPush={(path) => history.push(path)}
    />
  );
}

export default NotificationInbox;
